// react
import { useCallback, useEffect, useRef, useMemo, useState } from 'react';

// i18n

// mui
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	alpha,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	Fab,
	IconButton,
	keyframes,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableRow,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	AutoAwesome as IconAI,
	Business as IconBeneficiary,
	CheckCircle as IconCheck,
	ChevronLeft as IconPrev,
	ChevronRight as IconNext,
	Close as IconClose,
	Code as IconCode,
	Description as IconDoc,
	Download as IconDownload,
	Edit as IconEdit,
	ErrorOutline as IconError,
	ExpandMore as IconExpand,
	HourglassEmpty as IconPending,
	Image as IconImage,
	InsertDriveFile as IconFile,
	OpenInFull as IconExpandChat,
	CloseFullscreen as IconCollapseChat,
	Payment as IconPayment,
	Person as IconPerson,
	PictureAsPdf as IconPdf,
	Receipt as IconInvoice,
	ChatBubbleOutline as IconChat,
	Save as IconSave,
	SwapVert as IconTransaction,
	TableChart as IconXlsx,
	TextSnippet as IconText,
	Warning as IconWarning,
} from '@mui/icons-material';

// data
import { COLORS, ContractDocument } from '../../../data';

// types
import {
	AiDocumentResultBlob,
	DocumentAiInsights,
	PersonaInsight,
} from '../../pages/ProjectV2/Documents/documentTypes';

// services
import {
	fetchAiBlob,
	fetchAiBlobObjectUrl,
	fetchAiDocumentResults,
	sendChat,
} from '../../../services/aiApi';
import type { LlmMessage } from '../../../services/aiApi';

// components
import { AiChat, type LlmHistoryMessage } from '../../ui/AiChat/AiChat';
import { AiDisclaimerBanner } from '../../ui/AiDisclaimerBanner/AiDisclaimerBanner';

// sse
import { useSSE } from '../../../utils/sse/useSSE';

// Helpers — extension-based icon + label

/** Extract the file extension (lowercase) from a filename */
const getFileExtension = (filename: string): string =>
	(filename.split('.').pop() || '').toLowerCase();

/** Render an MUI icon based on the file extension in the filename */
const getFileIconFromName = (filename: string, size = 32) => {
	const ext = getFileExtension(filename);
	switch (ext) {
		case 'pdf':
			return <IconPdf sx={{ color: '#d32f2f', fontSize: size }} />;
		case 'xls':
		case 'xlsx':
			return <IconXlsx sx={{ color: '#16a34a', fontSize: size }} />;
		case 'csv':
			return <IconXlsx sx={{ color: '#0d9488', fontSize: size }} />;
		case 'doc':
		case 'docx':
			return <IconDoc sx={{ color: '#2563eb', fontSize: size }} />;
		case 'jpg':
		case 'jpeg':
		case 'png':
		case 'gif':
		case 'webp':
		case 'svg':
			return <IconImage sx={{ color: '#ea580c', fontSize: size }} />;
		case 'json':
		case 'xml':
		case 'html':
			return <IconCode sx={{ color: '#7c3aed', fontSize: size }} />;
		case 'txt':
		case 'md':
			return <IconText sx={{ color: '#6b7280', fontSize: size }} />;
		default:
			return <IconFile sx={{ color: '#2563eb', fontSize: size }} />;
	}
};

/** Human-readable file type label from extension */
const getFileTypeLabel = (filename: string): string => {
	const ext = getFileExtension(filename);
	const map: Record<string, string> = {
		pdf: 'PDF Document',
		doc: 'Word Document',
		docx: 'Word Document',
		xls: 'Excel Spreadsheet',
		xlsx: 'Excel Spreadsheet',
		csv: 'CSV File',
		jpg: 'JPEG Image',
		jpeg: 'JPEG Image',
		png: 'PNG Image',
		gif: 'GIF Image',
		txt: 'Text File',
		json: 'JSON File',
		xml: 'XML File',
		html: 'HTML File',
	};
	return map[ext] || (ext ? `${ext.toUpperCase()} File` : 'File');
};

// Source-type styling (mirrors the table SOURCE_TYPE_CONFIG)

const SOURCE_TYPE_CONFIG: Record<
	string,
	{ label: string; icon: React.ComponentType<{ sx?: object }>; color: string }
> = {
	contract: { label: 'Contract', icon: IconDoc, color: '#2563eb' },
	invoice: { label: 'Invoice', icon: IconInvoice, color: '#ea580c' },
	transaction: { label: 'Transaction', icon: IconTransaction, color: '#16a34a' },
	payment: { label: 'Payment', icon: IconPayment, color: '#7c3aed' },
	activity: { label: 'Activity', icon: IconActivity, color: '#d97706' },
	beneficiary: { label: 'Beneficiary', icon: IconBeneficiary, color: '#0d9488' },
};

// AI Processing Pipeline Stage Labels

const STAGE_LABELS: Record<string, { label: string; color: string }> = {
	bronze: { label: 'Uploaded', color: '#92400e' },
	silver: { label: 'Parsed', color: '#6b7280' },
	pregold: { label: 'Enriched', color: '#2563eb' },
	'pre-gold': { label: 'Enriched', color: '#2563eb' },
	gold: { label: 'AI Analysis', color: '#d97706' },
};

const STAGE_ORDER = ['bronze', 'silver', 'pregold', 'gold'];

/** Subtle pulse animation for processing-in-progress skeleton cards */
const pulseAnimation = keyframes`
	0%, 100% { opacity: 1; }
	50% { opacity: 0.6; }
`;

/**
 * Safely convert any value to a React-renderable string.
 * Prevents "Objects are not valid as a React child" errors when
 * the AI returns nested objects (e.g. spreadsheet rows) instead of plain strings.
 */
const safeString = (v: unknown): string => {
	if (v === null || v === undefined) return '';
	if (typeof v === 'string') return v;
	if (typeof v === 'number' || typeof v === 'boolean') return String(v);
	try {
		return JSON.stringify(v, null, 2);
	} catch {
		return String(v);
	}
};

// Doc-type options for the edit select

// Edit payload — passed up by onEditSave

export interface DocumentEditPayload {
	/** Document id */
	id: string;
	tags: string[];
}

// Props & Types

export interface ViewerDocumentInfo {
	/** Document ID (for edit) */
	id?: string;
	/** Blob filename */
	name: string;
	/** Tags as string array */
	tags?: string[];
	/** Source type (contract, activity, …) — shown as chip */
	sourceType?: string;
	/** Reference / source label (e.g. "Project Alpha") */
	sourceLabel?: string;
	/** Human-readable size */
	size?: string;
	/** ISO date string */
	uploadedAt?: string;
	/** Uploader display name */
	uploadedBy?: string;
	/** Job ID for AI processing results */
	jobId?: string;
}

export interface DialogDocumentViewerProps {
	currentIndex: number;
	documents: ContractDocument[];
	isOpen: boolean;
	onClose: () => void;
	onNavigate: (index: number) => void;
	/** Blob filenames for read_blob. If provided, viewer fetches real blob content. */
	blobFilenames?: string[];
	/** Rich document info that mirrors the table columns (source, reference, etc.) */
	documentInfos?: ViewerDocumentInfo[];
	/** Initial tab to show: 'document' or 'insights'. Defaults to 'document'. */
	initialTab?: 'document' | 'insights';
	/** Callback when tab changes (for URL param sync) */
	onTabChange?: (tab: 'document' | 'insights') => void;
	/** If provided, enables inline editing in the sidebar. Called with the edit payload on Save. */
	onEditSave?: (payload: DocumentEditPayload) => Promise<void>;
	/** Whether to open in edit mode (driven by URL param) */
	initialEditing?: boolean;
	/** Callback when edit mode changes (for URL param sync) */
	onEditChange?: (editing: boolean) => void;
}

// Component

export const DialogDocumentViewer = ({
	currentIndex,
	documents,
	isOpen,
	onClose,
	onNavigate,
	blobFilenames,
	documentInfos,
	initialTab = 'document',
	onTabChange,
	onEditSave,
	initialEditing = false,
	onEditChange,
}: DialogDocumentViewerProps) => {
	// hooks
	const theme = useTheme();

	// Tab state - 'document' or 'insights'
	const [activeTab, setActiveTab] = useState<'document' | 'insights'>('document');

	// Blob loading state (document preview)
	const [blobObjectUrl, setBlobObjectUrl] = useState<string | null>(null);
	const [blobContentType, setBlobContentType] = useState<string | null>(null);
	const [blobLoading, setBlobLoading] = useState(false);
	const [blobError, setBlobError] = useState<string | null>(null);

	// AI Insights state
	const [insightsBlobs, setInsightsBlobs] = useState<AiDocumentResultBlob[]>([]);
	const [insightsData, setInsightsData] = useState<DocumentAiInsights | null>(null);
	const [insightsLoading, setInsightsLoading] = useState(false);
	const [insightsLoaded, setInsightsLoaded] = useState(false);
	// Track which persona sections have raw JSON visible (by index)
	const [expandedRawJson, setExpandedRawJson] = useState<Set<number>>(new Set());
	// Show non-specialist insight boxes (Extracted/Enriched text, etc.) by request hidden by default
	const [showAdvancedInsights, setShowAdvancedInsights] = useState(false);

	// Chat popover state
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [isChatExpanded, setIsChatExpanded] = useState(false);
	const [isChatLoading, setIsChatLoading] = useState(false);
	const [hasUnreadResponse, setHasUnreadResponse] = useState(false);
	const isChatOpenRef = useRef(false);

	// Keep ref in sync
	useEffect(() => {
		isChatOpenRef.current = isChatOpen;
	}, [isChatOpen]);

	// Stable callback for chat response — avoids stale closure in AiChat's useCallback
	const handleChatResponse = useCallback(() => {
		if (!isChatOpenRef.current) {
			setHasUnreadResponse(true);
		}
	}, []);

	// Close handler — warn if AI is running or has unread response
	const handleDialogClose = useCallback(() => {
		if (isChatLoading) {
			if (!window.confirm('A response is still being generated. Close anyway?')) return;
		} else if (hasUnreadResponse) {
			if (!window.confirm('You have an unread response. Close anyway?')) return;
		}
		onClose();
	}, [isChatLoading, hasUnreadResponse, onClose]);

	// Inline editing state — driven by parent URL param via initialEditing
	const [isEditing, setIsEditing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [editForm, setEditForm] = useState({ tags: '' });
	// Local tags override — shown after save before parent refetch completes
	const [savedTagsOverride, setSavedTagsOverride] = useState<string[] | null>(null);

	// Computed
	const currentDocument = documents[currentIndex] || null;
	const hasPrev = currentIndex > 0;
	const hasNext = currentIndex < documents.length - 1;
	const currentBlobFilename = blobFilenames?.[currentIndex];
	const rawInfo = documentInfos?.[currentIndex];
	// Apply local override so tags update immediately after save
	const currentInfo =
		rawInfo && savedTagsOverride ? { ...rawInfo, tags: savedTagsOverride } : rawInfo;
	const currentJobId = currentInfo?.jobId;

	// Ref to hold the loadInsights function so SSE handler can call it
	const refLoadInsights = useRef<((jobId: string) => Promise<void>) | null>(null);

	// SSE subscription — listen for docAI events matching the current jobId
	useSSE({
		filter: (notification) =>
			notification.type === 'docAI' &&
			!!currentJobId &&
			notification.transactionId === currentJobId,
		onNotification: (notification) => {
			console.log('[DialogDocumentViewer] docAI SSE event received:', notification);
			if (currentJobId && activeTab === 'insights' && refLoadInsights.current) {
				// Re-fetch insights when a new processing stage completes
				refLoadInsights.current(currentJobId);
			} else if (currentJobId) {
				// If on document tab, mark insights as stale so they reload on tab switch
				setInsightsLoaded(false);
			}
		},
	});

	// Load blob content when document changes
	const loadBlob = useCallback(async (filename: string) => {
		setBlobLoading(true);
		setBlobError(null);
		setBlobObjectUrl(null);
		setBlobContentType(null);

		try {
			const { objectUrl, contentType } = await fetchAiBlobObjectUrl('bronze', filename);
			setBlobObjectUrl(objectUrl);
			setBlobContentType(contentType);
		} catch (err) {
			setBlobError(err instanceof Error ? err.message : 'Failed to load document');
		} finally {
			setBlobLoading(false);
		}
	}, []);

	useEffect(() => {
		if (isOpen && currentBlobFilename) {
			loadBlob(currentBlobFilename);
		}
		// Cleanup object URL on unmount / doc change
		return () => {
			if (blobObjectUrl) {
				URL.revokeObjectURL(blobObjectUrl);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, currentBlobFilename, loadBlob]);

	// Reset state when dialog closes, set initial tab when it opens
	useEffect(() => {
		if (!isOpen) {
			if (blobObjectUrl) URL.revokeObjectURL(blobObjectUrl);
			setBlobObjectUrl(null);
			setBlobContentType(null);
			setBlobError(null);
			setBlobLoading(false);
			// Reset AI state
			setActiveTab('document');
			setInsightsBlobs([]);
			setInsightsData(null);
			setInsightsLoading(false);
			setInsightsLoaded(false);
			setExpandedRawJson(new Set());
			// Reset edit + chat state
			setIsEditing(false);
			setIsSaving(false);
			setSavedTagsOverride(null);
			setIsChatOpen(false);
			setIsChatLoading(false);
			setHasUnreadResponse(false);
		} else {
			// Set initial tab when dialog opens
			setActiveTab(initialTab);
			// Sync edit state from URL param
			if (initialEditing && onEditSave) {
				const info = documentInfos?.[currentIndex];
				setEditForm({
					tags: (info?.tags || []).join(', '),
				});
				setIsEditing(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, initialTab, initialEditing]);

	// Reset AI + edit state when document changes
	useEffect(() => {
		setInsightsBlobs([]);
		setInsightsData(null);
		setInsightsLoaded(false);
		setExpandedRawJson(new Set());
		setIsEditing(false);
		setIsSaving(false);
		setSavedTagsOverride(null);
	}, [currentIndex]);

	// Load AI insights when the insights tab is activated (lazy loading)
	const loadInsights = useCallback(async (jobId: string) => {
		setInsightsLoading(true);
		// Don't clear existing data — keep it visible during SSE re-fetches
		// Only clear on first load (when there's nothing to show yet)

		try {
			// 1. Fetch document processing results (blobs)
			const res = await fetchAiDocumentResults(jobId);
			const blobs = res.blobs;
			setInsightsBlobs(blobs);

			// 2. Find ALL JSON or TXT blobs from any stage (silver, pre-gold, gold) and fetch their content
			const aiBlobs = blobs.filter(
				(b) => b.blob_name?.endsWith('.json') || b.blob_name?.endsWith('.txt')
			);

			// Collect all persona insights from all blobs
			const allPersonas: PersonaInsight[] = [];

			for (const aiBlob of aiBlobs) {
				if (!aiBlob.blob_name) continue;

				const slashIdx = aiBlob.blob_name.indexOf('/');
				const container =
					slashIdx > 0 ? aiBlob.blob_name.slice(0, slashIdx) : aiBlob.stage || 'gold';
				const blobPath = slashIdx > 0 ? aiBlob.blob_name.slice(slashIdx + 1) : aiBlob.blob_name;

				try {
					const blobRes = await fetchAiBlob(container, blobPath);
					const content = blobRes.content;

					console.log(
						`[AI Insights] Fetched ${aiBlob.stage} blob:`,
						aiBlob.blob_name,
						typeof content,
						content
					);

					// Parse the blob content (expected to be an array of persona objects for gold, or other formats)
					if (Array.isArray(content)) {
						for (const item of content) {
							if (item && typeof item === 'object' && 'Persona' in item) {
								allPersonas.push({
									persona: ((item as Record<string, unknown>).Persona as string) || 'Unknown',
									summary: ((item as Record<string, unknown>).Summary as string) || '',
									checklist: Array.isArray((item as Record<string, unknown>).Checklist)
										? ((item as Record<string, unknown>).Checklist as string[])
										: undefined,
									keyInformationTable:
										typeof (item as Record<string, unknown>).KeyInformationTable === 'object'
											? ((item as Record<string, unknown>).KeyInformationTable as Record<
													string,
													string
												>)
											: undefined,
									disbursementConditions:
										((item as Record<string, unknown>).DisbursementConditions as string) ||
										undefined,
									timelines: ((item as Record<string, unknown>).Timelines as string) || undefined,
									responsibleParties:
										((item as Record<string, unknown>).ResponsibleParties as string) || undefined,
									keyRisks: ((item as Record<string, unknown>).KeyRisks as string) || undefined,
									stage: aiBlob.stage,
									blobName: aiBlob.blob_name,
									rawJson: item,
								});
							} else if (item && typeof item === 'object') {
								// Non-persona object in array - show as raw data section
								allPersonas.push({
									persona: `${STAGE_LABELS[aiBlob.stage || '']?.label || aiBlob.stage?.toUpperCase() || 'Stage'} Data`,
									summary: '',
									stage: aiBlob.stage,
									blobName: aiBlob.blob_name,
									rawJson: item,
								});
							}
						}
					} else if (typeof content === 'string') {
						// String content (e.g., extracted text from TXT files)
						allPersonas.push({
							persona: `${STAGE_LABELS[aiBlob.stage || '']?.label || aiBlob.stage?.toUpperCase() || 'Stage'} Extracted Text`,
							summary: content.length > 500 ? content.slice(0, 500) + '...' : content,
							stage: aiBlob.stage,
							blobName: aiBlob.blob_name,
							rawJson: { text: content },
						});
					} else if (content && typeof content === 'object') {
						// Single object format
						const obj = content as Record<string, unknown>;
						if ('Persona' in obj) {
							allPersonas.push({
								persona: (obj.Persona as string) || 'Analysis',
								summary: (obj.Summary as string) || '',
								checklist: Array.isArray(obj.Checklist) ? (obj.Checklist as string[]) : undefined,
								keyInformationTable:
									typeof obj.KeyInformationTable === 'object'
										? (obj.KeyInformationTable as Record<string, string>)
										: undefined,
								disbursementConditions: (obj.DisbursementConditions as string) || undefined,
								timelines: (obj.Timelines as string) || undefined,
								responsibleParties: (obj.ResponsibleParties as string) || undefined,
								keyRisks: (obj.KeyRisks as string) || undefined,
								stage: aiBlob.stage,
								blobName: aiBlob.blob_name,
								rawJson: obj,
							});
						} else {
							// Generic JSON object - show with stage label
							allPersonas.push({
								persona: `${STAGE_LABELS[aiBlob.stage || '']?.label || aiBlob.stage?.toUpperCase() || 'Stage'} Output`,
								summary:
									(obj.summary as string) ||
									(obj.description as string) ||
									(obj.text as string) ||
									'',
								stage: aiBlob.stage,
								blobName: aiBlob.blob_name,
								rawJson: obj,
							});
						}
					}
				} catch (blobErr) {
					console.error('Failed to fetch blob content:', aiBlob.blob_name, blobErr);
				}
			}

			console.log(`[AI Insights] Total personas collected:`, allPersonas.length, allPersonas);

			if (allPersonas.length > 0) {
				setInsightsData({ personas: allPersonas });
			}
		} catch (err) {
			console.error('Failed to fetch document results:', err);
		} finally {
			setInsightsLoading(false);
			setInsightsLoaded(true);
		}
	}, []);

	// Keep the ref in sync so SSE handler can call loadInsights
	refLoadInsights.current = loadInsights;

	// Trigger insights load when tab changes to insights or chat opens
	useEffect(() => {
		if ((activeTab === 'insights' || isChatOpen) && currentJobId && !insightsLoaded) {
			loadInsights(currentJobId);
		}
	}, [activeTab, isChatOpen, currentJobId, insightsLoaded, loadInsights]);

	// Edit handlers
	const handleStartEdit = useCallback(() => {
		const info = documentInfos?.[currentIndex];
		const tags = savedTagsOverride || info?.tags || [];
		setEditForm({
			tags: tags.join(', '),
		});
		setIsEditing(true);
		onEditChange?.(true);
	}, [currentIndex, documentInfos, savedTagsOverride, onEditChange]);

	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setIsSaving(false);
		onEditChange?.(false);
	}, [onEditChange]);

	const handleSaveEdit = useCallback(async () => {
		const info = documentInfos?.[currentIndex];
		if (!onEditSave || !info?.id) return;
		setIsSaving(true);
		try {
			const parsedTags = editForm.tags
				.split(',')
				.map((t) => t.trim())
				.filter(Boolean);
			await onEditSave({
				id: info.id,
				tags: parsedTags,
			});
			setSavedTagsOverride(parsedTags);
			setIsEditing(false);
			onEditChange?.(false);
		} catch (err) {
			console.error('Edit save failed:', err);
		} finally {
			setIsSaving(false);
		}
	}, [currentIndex, documentInfos, editForm, onEditSave, onEditChange]);

	// Helpers
	const formatDocumentDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		});
	};

	// Resolved display values — prefer rich documentInfos, fall back to ContractDocument
	const docName = currentInfo?.name || currentDocument?.name || '';
	const docSourceType = currentInfo?.sourceType || '';
	const docSourceLabel = currentInfo?.sourceLabel || '';
	const docSize = currentInfo?.size || currentDocument?.size || '';
	const docUploadedAt = currentInfo?.uploadedAt || currentDocument?.uploadedAt || '';
	const docUploadedBy = currentInfo?.uploadedBy || currentDocument?.uploadedBy || '';
	const sourceConfig = SOURCE_TYPE_CONFIG[docSourceType];
	const sourceColor = sourceConfig?.color || theme.palette.grey[500];

	const documentContextSummary = useMemo(() => {
		const sections: string[] = [];

		sections.push(`Document: ${docName || 'Unknown'}`);
		if (docSourceType) sections.push(`Source Type: ${docSourceType}`);
		if (docSourceLabel) sections.push(`Source Reference: ${docSourceLabel}`);
		if (docUploadedAt) sections.push(`Uploaded At: ${docUploadedAt}`);
		if (docUploadedBy) sections.push(`Uploaded By: ${docUploadedBy}`);

		if (insightsBlobs.length > 0) {
			const stageList = insightsBlobs
				.map((b) => (b.stage === 'pre-gold' ? 'pregold' : b.stage))
				.filter(Boolean)
				.join(', ');
			sections.push(`Available Processing Stages: ${stageList || 'none'}`);
		}

		if (insightsData?.personas?.length) {
			const personaLines = insightsData.personas.slice(0, 12).map((persona, idx) => {
				const summary = safeString(persona.summary || '').slice(0, 800);
				const checklist = persona.checklist
					?.slice(0, 6)
					.map((item) => safeString(item))
					.join(' | ');
				const stage = persona.stage || 'unknown';
				return [
					`# Persona ${idx + 1}`,
					`Stage: ${stage}`,
					`Persona: ${safeString(persona.persona)}`,
					`Summary: ${summary}`,
					checklist ? `Checklist: ${checklist}` : '',
				]
					.filter(Boolean)
					.join('\n');
			});

			sections.push(`Insights:\n${personaLines.join('\n\n')}`);
		}

		const combined = sections.join('\n\n');
		return combined.length > 12000 ? `${combined.slice(0, 12000)}\n\n[Truncated]` : combined;
	}, [
		docName,
		docSourceType,
		docSourceLabel,
		docUploadedAt,
		docUploadedBy,
		insightsBlobs,
		insightsData,
	]);

	const handleDocumentChatSend = useCallback(
		async ({
			message,
			history,
		}: {
			message: string;
			history: LlmHistoryMessage[];
			onChunk?: (chunk: string) => void;
		}): Promise<{
			response: string;
			meta?: { model?: string; duration_ms?: number; service?: string };
		}> => {
			if (!currentJobId) {
				return {
					response:
						'This document has no AI processing results yet. Please wait for processing to complete, then ask again.',
				};
			}

			if (!documentContextSummary || !insightsData) {
				return {
					response:
						'I do not have document context loaded yet. Open the AI insights view first and try again in a moment.',
				};
			}

			const systemPrompt = [
				'You are a document-grounded assistant for BLDT spend plans.',
				'Answer ONLY using the provided document context.',
				'If context is insufficient, clearly say what is missing and do not invent details.',
				'Prefer concise bullet points and include stage/persona references where useful.',
				`Context follows:\n${documentContextSummary}`,
			].join(' ');

			// Build conversation history (without system prompt — passed separately)
			const messages: LlmMessage[] = [
				...(history as LlmMessage[]),
				{ role: 'user', content: message },
			];

			const result = await sendChat({
				messages,
				system_prompt: systemPrompt,
			});

			return {
				response: result.reply,
				meta: {
					model: result.model,
					duration_ms: result.duration_ms,
					service: result.service,
				},
			};
		},
		[currentJobId, documentContextSummary, insightsData]
	);

	// Handlers
	const handlePrev = () => {
		if (hasPrev) {
			onNavigate(currentIndex - 1);
		}
	};

	const handleNext = () => {
		if (hasNext) {
			onNavigate(currentIndex + 1);
		}
	};

	const handleDownload = () => {
		if (blobObjectUrl && currentDocument) {
			// Sanitize the filename: drop path separators, control chars, and
			// HTML-special characters so the value cannot influence any DOM
			// context that might later interpret it as markup (CWE-80).
			const raw = currentDocument.name || 'download';
			let safeName = '';
			for (let i = 0; i < raw.length && safeName.length < 255; i++) {
				const code = raw.charCodeAt(i);
				const ch = raw[i];
				const isControl = code < 32 || code === 127;
				const isUnsafe =
					ch === '/' ||
					ch === '\\' ||
					ch === '<' ||
					ch === '>' ||
					ch === '"' ||
					ch === "'" ||
					ch === '`';
				safeName += isControl || isUnsafe ? '_' : ch;
			}
			const a = document.createElement('a');
			a.href = blobObjectUrl;
			a.download = safeName || 'download';
			a.rel = 'noopener';
			a.click();
		}
	};

	// Render the right sidebar with document details + optional bottom CTA
	const renderDetailsSidebar = (bottomCta?: React.ReactNode) => (
		<Box
			sx={{
				borderLeft: `1px solid ${theme.palette.divider}`,
				width: 300,
				minWidth: 260,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}>
			<Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
				{/* Header + edit controls */}
				<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
						Document Details
					</Typography>
					{isEditing ? (
						<Stack direction="row" spacing={0.5}>
							<IconButton
								size="small"
								onClick={handleSaveEdit}
								disabled={isSaving}
								sx={{ color: 'success.main' }}>
								{isSaving ? <CircularProgress size={16} /> : <IconSave sx={{ fontSize: 18 }} />}
							</IconButton>
							<IconButton
								size="small"
								onClick={handleCancelEdit}
								disabled={isSaving}
								sx={{ color: 'text.secondary' }}>
								<IconClose sx={{ fontSize: 18 }} />
							</IconButton>
						</Stack>
					) : (
						onEditSave && (
							<IconButton size="small" onClick={handleStartEdit}>
								<IconEdit sx={{ fontSize: 18 }} />
							</IconButton>
						)
					)}
				</Stack>

				{/* Detail fields — vertical stack */}
				<Stack spacing={2}>
					{/* File Type */}
					<Box>
						<Typography variant="caption" color="text.secondary">
							File Type
						</Typography>
						<Stack direction="row" alignItems="center" spacing={0.5}>
							{getFileIconFromName(docName, 16)}
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{getFileTypeLabel(docName)}
							</Typography>
						</Stack>
					</Box>

					{/* Tags */}
					<Box>
						<Typography variant="caption" color="text.secondary">
							Tags
						</Typography>
						{isEditing ? (
							<TextField
								value={editForm.tags}
								onChange={(e) => setEditForm((prev) => ({ ...prev, tags: e.target.value }))}
								fullWidth
								size="small"
								variant="outlined"
								placeholder="comma-separated"
								helperText="Separate with commas"
								sx={{ mt: 0.5 }}
							/>
						) : (
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
								{currentInfo?.tags && currentInfo.tags.length > 0 ? (
									currentInfo.tags.map((tag) => (
										<Chip
											key={tag}
											label={tag}
											size="small"
											variant="outlined"
											sx={{ height: 24, fontWeight: 500 }}
										/>
									))
								) : (
									<Typography variant="body2" color="text.secondary">
										—
									</Typography>
								)}
							</Box>
						)}
					</Box>

					{/* Source */}
					{sourceConfig && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								Source
							</Typography>
							<Box sx={{ mt: 0.25 }}>
								<Chip
									icon={<sourceConfig.icon sx={{ fontSize: '14px !important' }} />}
									label={sourceConfig.label}
									size="small"
									sx={{
										bgcolor: alpha(sourceColor, 0.1),
										color: sourceColor,
										fontWeight: 500,
										height: 24,
										'& .MuiChip-icon': { color: sourceColor },
									}}
								/>
							</Box>
						</Box>
					)}

					{/* Reference */}
					{docSourceLabel && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								Reference
							</Typography>
							<Typography
								variant="body2"
								sx={{
									fontWeight: 500,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}>
								{docSourceLabel}
							</Typography>
						</Box>
					)}

					{/* File Size */}
					<Box>
						<Typography variant="caption" color="text.secondary">
							File Size
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							{docSize || '—'}
						</Typography>
					</Box>

					{/* Uploaded By */}
					<Box>
						<Typography variant="caption" color="text.secondary">
							Uploaded By
						</Typography>
						<Typography
							variant="body2"
							sx={{
								fontWeight: 500,
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}>
							{docUploadedBy || '—'}
						</Typography>
					</Box>

					{/* Uploaded */}
					{docUploadedAt && (
						<Box>
							<Typography variant="caption" color="text.secondary">
								Uploaded
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{formatDocumentDate(docUploadedAt)}
							</Typography>
						</Box>
					)}
				</Stack>
			</Box>

			{/* Bottom CTA */}
			{bottomCta && (
				<Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>{bottomCta}</Box>
			)}
		</Box>
	);

	// Render the content viewer area based on content type
	const renderContentViewer = () => {
		// Loading state
		if (blobLoading) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						gap: 2,
					}}>
					<CircularProgress size={48} />
					<Typography variant="body2" color="text.secondary">
						Loading document...
					</Typography>
				</Box>
			);
		}

		// Error state
		if (blobError) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						gap: 2,
					}}>
					<IconError sx={{ fontSize: 48, color: 'error.main' }} />
					<Typography
						variant="body2"
						color="error.main"
						sx={{ maxWidth: 400, textAlign: 'center' }}>
						{blobError}
					</Typography>
					<Button
						variant="outlined"
						size="small"
						onClick={() => currentBlobFilename && loadBlob(currentBlobFilename)}>
						Retry
					</Button>
				</Box>
			);
		}

		// Blob loaded — render based on content type
		if (blobObjectUrl && blobContentType) {
			// PDF
			if (blobContentType.includes('pdf')) {
				return (
					<iframe
						src={blobObjectUrl}
						title={currentDocument?.name || 'Document'}
						style={{ border: 'none', height: '100%', width: '100%' }}
					/>
				);
			}

			// Images
			if (blobContentType.startsWith('image/')) {
				return (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							p: 2,
							overflow: 'auto',
						}}>
						<img
							src={blobObjectUrl}
							alt={currentDocument?.name || 'Document'}
							style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
						/>
					</Box>
				);
			}

			// Text / JSON / CSV / HTML
			if (
				blobContentType.startsWith('text/') ||
				blobContentType.includes('json') ||
				blobContentType.includes('csv')
			) {
				return (
					<iframe
						src={blobObjectUrl}
						title={currentDocument?.name || 'Document'}
						style={{ border: 'none', height: '100%', width: '100%' }}
					/>
				);
			}

			// Word / Excel / other Office — use object embed fallback
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						gap: 2,
					}}>
					{getFileIconFromName(docName, 48)}
					<Typography variant="body1" sx={{ fontWeight: 500 }}>
						{docName}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Preview not available for this file type ({blobContentType})
					</Typography>
					<Button
						variant="contained"
						size="small"
						startIcon={<IconDownload />}
						onClick={handleDownload}>
						Download to View
					</Button>
				</Box>
			);
		}

		// No blob filename provided — show placeholder (legacy static mode)
		if (!currentBlobFilename) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						gap: 2,
					}}>
					{getFileIconFromName(docName, 48)}
					<Typography variant="body1" sx={{ fontWeight: 500 }}>
						{docName}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						No blob available for preview
					</Typography>
				</Box>
			);
		}

		return null;
	};

	// Render AI Insights content
	const renderInsightsContent = () => {
		// Normalize stage names: 'pre-gold' -> 'pregold' to match STAGE_ORDER
		const completedStages = new Set(
			insightsBlobs.map((b) => (b.stage === 'pre-gold' ? 'pregold' : b.stage))
		);
		// Bronze is always complete if we have any AI results (document was uploaded)
		if (insightsBlobs.length > 0) {
			completedStages.add('bronze');
		}

		// Initial loading state — show full skeleton only when we have NO data yet
		if (insightsLoading && !insightsData && insightsBlobs.length === 0) {
			return (
				<Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
					<Stack spacing={3}>
						{/* Pipeline skeleton */}
						<Paper
							elevation={0}
							sx={{
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 2,
								p: 2.5,
							}}>
							<Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
							<Stack direction="row" spacing={2} alignItems="center">
								{STAGE_ORDER.map((stage) => (
									<Stack key={stage} alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
										<Skeleton variant="circular" width={40} height={40} />
										<Skeleton variant="text" width={60} />
									</Stack>
								))}
							</Stack>
						</Paper>
						{/* Persona accordion skeletons */}
						{[1, 2].map((i) => (
							<Paper
								key={i}
								elevation={0}
								sx={{
									border: '1px solid',
									borderColor: 'divider',
									borderRadius: 3,
									overflow: 'hidden',
									animation: `${pulseAnimation} 2s ease-in-out infinite`,
								}}>
								<Box sx={{ px: 3, py: 2, bgcolor: alpha('#2563eb', 0.04) }}>
									<Stack direction="row" alignItems="center" spacing={1.5}>
										<Skeleton variant="circular" width={32} height={32} animation="wave" />
										<Box sx={{ flex: 1 }}>
											<Skeleton variant="text" width={200} height={24} animation="wave" />
											<Skeleton variant="text" width={120} height={18} animation="wave" />
										</Box>
									</Stack>
								</Box>
								<Box sx={{ px: 3, py: 2.5 }}>
									<Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} animation="wave" />
									<Skeleton variant="text" width="100%" animation="wave" />
									<Skeleton variant="text" width="90%" animation="wave" />
									<Skeleton variant="text" width="75%" animation="wave" />
									<Box sx={{ mt: 2.5 }}>
										<Skeleton
											variant="text"
											width={120}
											height={20}
											sx={{ mb: 1 }}
											animation="wave"
										/>
										<Skeleton
											variant="rectangular"
											width="100%"
											height={100}
											animation="wave"
											sx={{ borderRadius: 1 }}
										/>
									</Box>
								</Box>
							</Paper>
						))}
					</Stack>
				</Box>
			);
		}

		// No job ID
		if (!currentJobId) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						height: '100%',
						gap: 2,
					}}>
					<IconPending sx={{ fontSize: 48, color: 'text.disabled' }} />
					<Typography variant="body1" sx={{ fontWeight: 600 }}>
						No AI processing results
					</Typography>
					<Typography variant="body2" color="text.secondary">
						This document has not been processed through the AI pipeline.
					</Typography>
				</Box>
			);
		}

		return (
			<Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
				<Stack spacing={3}>
					{/* AI-Assisted Information disclaimer — surfaces the shared
					    disclaimer at the top of the AI insights region. */}
					<AiDisclaimerBanner />

					{/* Waiting for processing — job exists but no blobs/data yet */}
					{!insightsLoading && insightsBlobs.length === 0 && !insightsData && (
						<>
							{/* Show the pipeline in all-pending state */}
							<Paper
								elevation={0}
								sx={{
									bgcolor: alpha(theme.palette.background.default, 0.5),
									border: '1px solid',
									borderColor: 'divider',
									borderRadius: 2,
									p: 2.5,
								}}>
								<Typography
									variant="subtitle1"
									sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 2 }}>
									Processing Pipeline
								</Typography>
								<Stack direction="row" spacing={0} alignItems="center">
									{STAGE_ORDER.map((stageName, idx) => {
										const label = STAGE_LABELS[stageName] || { label: stageName, color: '#6b7280' };
										return (
											<Stack key={stageName} direction="row" alignItems="center" sx={{ flex: 1 }}>
												<Stack alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
													<Box
														sx={{
															width: 40,
															height: 40,
															borderRadius: '50%',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															bgcolor: 'action.disabledBackground',
															border: '2px solid',
															borderColor: 'divider',
														}}>
														<IconPending sx={{ fontSize: 20, color: 'text.disabled' }} />
													</Box>
													<Typography
														variant="body2"
														sx={{
															fontWeight: 400,
															color: 'text.disabled',
															fontSize: '1.25rem',
														}}>
														{label.label}
													</Typography>
												</Stack>
												{idx < STAGE_ORDER.length - 1 && (
													<Box sx={{ height: 2, flex: 0.5, bgcolor: 'divider', mt: -3 }} />
												)}
											</Stack>
										);
									})}
								</Stack>
							</Paper>

							<Paper
								elevation={0}
								sx={{
									bgcolor: alpha(theme.palette.info.main, 0.06),
									border: '1px solid',
									borderColor: alpha(theme.palette.info.main, 0.2),
									borderRadius: 2,
									p: 2.5,
								}}>
								<Stack direction="row" spacing={2} alignItems="center">
									<CircularProgress size={18} sx={{ color: '#7c3aed' }} />
									<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
										Document uploaded — AI processing is starting. Insights will appear
										automatically as each stage completes.
									</Typography>
								</Stack>
							</Paper>

							{/* Skeleton persona placeholders */}
							{[1, 2].map((i) => (
								<Paper
									key={i}
									elevation={0}
									sx={{
										border: '1px dashed',
										borderColor: alpha('#7c3aed', 0.3),
										borderRadius: 3,
										overflow: 'hidden',
										animation: `${pulseAnimation} 2s ease-in-out infinite`,
									}}>
									<Box sx={{ px: 3, py: 2, bgcolor: alpha('#7c3aed', 0.03) }}>
										<Stack direction="row" alignItems="center" spacing={1.5}>
											<Skeleton variant="circular" width={32} height={32} animation="wave" />
											<Box sx={{ flex: 1 }}>
												<Skeleton variant="text" width={180} height={24} animation="wave" />
												<Skeleton variant="text" width={100} height={18} animation="wave" />
											</Box>
										</Stack>
									</Box>
									<Box sx={{ px: 3, py: 2.5 }}>
										<Skeleton
											variant="text"
											width={80}
											height={20}
											animation="wave"
											sx={{ mb: 1 }}
										/>
										<Skeleton variant="text" width="100%" animation="wave" />
										<Skeleton variant="text" width="85%" animation="wave" />
										<Skeleton variant="text" width="60%" animation="wave" />
									</Box>
								</Paper>
							))}
						</>
					)}

					{/* Processing Pipeline */}
					{insightsBlobs.length > 0 && (
						<Paper
							elevation={0}
							sx={{
								bgcolor: alpha(theme.palette.background.default, 0.5),
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: 2,
								p: 2.5,
							}}>
							<Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 2 }}>
								Processing Pipeline
							</Typography>
							<Stack direction="row" spacing={0} alignItems="center">
								{STAGE_ORDER.map((stageName, idx) => {
									const isComplete = completedStages.has(stageName);
									const label = STAGE_LABELS[stageName] || { label: stageName, color: '#6b7280' };
									// Handle stage name variants: pregold -> pre-gold
									const stageResult = insightsBlobs.find(
										(b) =>
											b.stage === stageName || (stageName === 'pregold' && b.stage === 'pre-gold')
									);
									// For bronze, use the earliest blob's created_at as proxy
									const bronzeTime =
										stageName === 'bronze' && !stageResult && insightsBlobs.length > 0
											? insightsBlobs.reduce(
													(earliest, b) =>
														!earliest || (b.created_at && b.created_at < earliest)
															? b.created_at
															: earliest,
													insightsBlobs[0]?.created_at
												)
											: null;

									return (
										<Stack key={stageName} direction="row" alignItems="center" sx={{ flex: 1 }}>
											<Tooltip
												title={
													isComplete && (stageResult?.created_at || bronzeTime)
														? `Completed: ${new Date(stageResult?.created_at || bronzeTime || 0).toLocaleString()}`
														: isComplete
															? 'Completed'
															: 'Pending'
												}>
												<Stack alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
													<Box
														sx={{
															width: 40,
															height: 40,
															borderRadius: '50%',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															bgcolor: isComplete
																? alpha(label.color, 0.15)
																: 'action.disabledBackground',
															border: '2px solid',
															borderColor: isComplete ? label.color : 'divider',
															transition: 'all 0.3s',
														}}>
														{isComplete ? (
															<IconCheck sx={{ fontSize: 22, color: label.color }} />
														) : (
															<IconPending sx={{ fontSize: 20, color: 'text.disabled' }} />
														)}
													</Box>
													<Typography
														variant="body2"
														sx={{
															fontWeight: isComplete ? 600 : 400,
															color: isComplete ? label.color : 'text.disabled',
															fontSize: '1.25rem',
														}}>
														{label.label}
													</Typography>
												</Stack>
											</Tooltip>
											{idx < STAGE_ORDER.length - 1 && (
												<Box
													sx={{
														height: 2,
														flex: 0.5,
														bgcolor: completedStages.has(STAGE_ORDER[idx + 1])
															? alpha(STAGE_LABELS[STAGE_ORDER[idx + 1]]?.color || '#6b7280', 0.4)
															: 'divider',
														transition: 'background-color 0.3s',
														mt: -3,
													}}
												/>
											)}
										</Stack>
									);
								})}
							</Stack>
						</Paper>
					)}

					{/* Processing in progress — banner + inline skeletons for upcoming content */}
					{!completedStages.has('gold') && insightsBlobs.length > 0 && (
						<Paper
							elevation={0}
							sx={{
								bgcolor: alpha(theme.palette.info.main, 0.06),
								border: '1px solid',
								borderColor: alpha(theme.palette.info.main, 0.2),
								borderRadius: 2,
								p: 2.5,
							}}>
							<Stack direction="row" spacing={2} alignItems="center">
								<CircularProgress size={18} sx={{ color: '#7c3aed' }} />
								<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
									Still processing&hellip; new insights will appear as each stage completes.
								</Typography>
							</Stack>
						</Paper>
					)}

					{/* Refreshing indicator — subtle inline bar when re-fetching with existing data */}
					{insightsLoading && (insightsData || insightsBlobs.length > 0) && (
						<Box
							sx={{
								height: 3,
								borderRadius: 2,
								overflow: 'hidden',
								bgcolor: alpha('#7c3aed', 0.12),
							}}>
							<Box
								sx={{
									height: '100%',
									bgcolor: '#7c3aed',
									animation: 'indeterminate 1.5s ease-in-out infinite',
									'@keyframes indeterminate': {
										'0%': { width: '0%', ml: '0%' },
										'50%': { width: '60%', ml: '20%' },
										'100%': { width: '0%', ml: '100%' },
									},
								}}
							/>
						</Box>
					)}

					{/* Personas Accordions */}
					{insightsData && insightsData.personas && insightsData.personas.length > 0 && (
						<>
							{(() => {
								const SPECIALIST_PERSONAS = new Set([
									'Financial Management Specialist',
									'Disbursement Specialist',
									'Procurement Specialist',
								]);
								const isSpecialist = (p: { persona?: string }) =>
									Boolean(p.persona && SPECIALIST_PERSONAS.has(p.persona));
								const allPersonas = insightsData.personas;
								const visiblePersonas = showAdvancedInsights
									? allPersonas
									: allPersonas.filter(isSpecialist);
								const hiddenCount = allPersonas.length - allPersonas.filter(isSpecialist).length;
								return (
									<>
										{hiddenCount > 0 && (
											<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
												<Button
													size="small"
													variant="text"
													onClick={() => setShowAdvancedInsights((v) => !v)}
													sx={{ textTransform: 'none' }}>
													{showAdvancedInsights
														? `Hide advanced data (${hiddenCount})`
														: `Show advanced data (${hiddenCount})`}
												</Button>
											</Box>
										)}
										{visiblePersonas.map((persona, idx) => (
											<Accordion
												key={idx}
												defaultExpanded
												sx={{
													bgcolor: 'background.paper',
													border: '1px solid',
													borderColor: 'divider',
													borderRadius: '12px !important',
													'&:before': { display: 'none' },
													boxShadow: 'none',
												}}>
												<AccordionSummary
													expandIcon={<IconExpand />}
													sx={{
														borderBottom: '1px solid',
														borderColor: 'divider',
														bgcolor: alpha('#2563eb', 0.04),
														borderRadius: '12px 12px 0 0',
														'&.Mui-expanded': {
															minHeight: 56,
														},
													}}>
													<Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
														<Box
															sx={{
																width: 32,
																height: 32,
																borderRadius: '50%',
																display: 'flex',
																alignItems: 'center',
																justifyContent: 'center',
																bgcolor: alpha('#2563eb', 0.12),
															}}>
															<IconPerson sx={{ fontSize: 18, color: '#2563eb' }} />
														</Box>
														<Box sx={{ flex: 1 }}>
															<Typography
																variant="subtitle1"
																sx={{ fontWeight: 700, color: '#2563eb' }}>
																{persona.persona}
															</Typography>
															<Stack
																direction="row"
																spacing={1}
																alignItems="center"
																flexWrap="wrap">
																{persona.blobName && (
																	<Chip
																		label={persona.blobName}
																		size="small"
																		sx={{
																			height: 'auto',
																			fontSize: '1.35rem',
																			fontFamily: 'monospace',
																			bgcolor: alpha(
																				STAGE_LABELS[persona.stage || 'gold']?.color || '#d97706',
																				0.12
																			),
																			color:
																				STAGE_LABELS[persona.stage || 'gold']?.color || '#d97706',
																			fontWeight: 600,
																			py: 0.5,
																		}}
																	/>
																)}
																{!persona.blobName && (
																	<Chip
																		label={
																			STAGE_LABELS[persona.stage || 'gold']?.label || 'AI Analysis'
																		}
																		size="small"
																		sx={{
																			height: 20,
																			fontSize: '0.7rem',
																			bgcolor: alpha(
																				STAGE_LABELS[persona.stage || 'gold']?.color || '#d97706',
																				0.12
																			),
																			color:
																				STAGE_LABELS[persona.stage || 'gold']?.color || '#d97706',
																			fontWeight: 600,
																		}}
																	/>
																)}
															</Stack>
														</Box>
													</Stack>
												</AccordionSummary>
												<AccordionDetails sx={{ p: 3 }}>
													<Stack spacing={3}>
														{/* Summary */}
														{persona.summary && (
															<Box>
																<Typography
																	variant="subtitle2"
																	sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
																	Summary
																</Typography>
																<Typography
																	variant="body1"
																	sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
																	{safeString(persona.summary)}
																</Typography>
															</Box>
														)}

														{/* Key Information Table */}
														{persona.keyInformationTable &&
															Object.keys(persona.keyInformationTable).length > 0 && (
																<Box>
																	<Typography
																		variant="subtitle2"
																		sx={{
																			fontWeight: 600,
																			color: 'text.secondary',
																			mb: 1.5,
																			fontSize: '1.25rem',
																		}}>
																		Key Information
																	</Typography>
																	<Table
																		size="small"
																		sx={{
																			'& td': { py: 1.25, px: 2 },
																			border: '1px solid',
																			borderColor: 'divider',
																			borderRadius: 1,
																		}}>
																		<TableBody>
																			{Object.entries(persona.keyInformationTable).map(
																				([key, value]) => (
																					<TableRow key={key}>
																						<TableCell
																							sx={{
																								fontWeight: 600,
																								width: '35%',
																								bgcolor: alpha('#2563eb', 0.03),
																								fontSize: '1.25rem',
																							}}>
																							{key.replace(/([A-Z])/g, ' $1').trim()}
																						</TableCell>
																						<TableCell
																							sx={{ fontSize: '1.25rem', whiteSpace: 'pre-wrap' }}>
																							{safeString(value)}
																						</TableCell>
																					</TableRow>
																				)
																			)}
																		</TableBody>
																	</Table>
																</Box>
															)}

														{/* Checklist */}
														{persona.checklist && persona.checklist.length > 0 && (
															<Box>
																<Typography
																	variant="subtitle2"
																	sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
																	Checklist
																</Typography>
																<Stack spacing={0.75}>
																	{persona.checklist.map((item, i) => (
																		<Stack
																			key={i}
																			direction="row"
																			spacing={1.5}
																			alignItems="flex-start">
																			<IconCheck
																				sx={{
																					fontSize: 18,
																					color: 'success.main',
																					mt: 0.25,
																					flexShrink: 0,
																				}}
																			/>
																			<Typography
																				variant="body1"
																				sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
																				{safeString(item)}
																			</Typography>
																		</Stack>
																	))}
																</Stack>
															</Box>
														)}

														{/* Disbursement Conditions */}
														{persona.disbursementConditions && (
															<Box>
																<Typography
																	variant="subtitle2"
																	sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
																	Disbursement Conditions
																</Typography>
																<Typography
																	variant="body1"
																	sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
																	{safeString(persona.disbursementConditions)}
																</Typography>
															</Box>
														)}

														{/* Timelines */}
														{persona.timelines && (
															<Box>
																<Typography
																	variant="subtitle2"
																	sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
																	Timelines
																</Typography>
																<Typography
																	variant="body1"
																	sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
																	{safeString(persona.timelines)}
																</Typography>
															</Box>
														)}

														{/* Responsible Parties */}
														{persona.responsibleParties && (
															<Box>
																<Typography
																	variant="subtitle2"
																	sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
																	Responsible Parties
																</Typography>
																<Typography
																	variant="body1"
																	sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
																	{safeString(persona.responsibleParties)}
																</Typography>
															</Box>
														)}

														{/* Key Risks */}
														{persona.keyRisks && (
															<Paper
																elevation={0}
																sx={{
																	bgcolor: alpha(theme.palette.warning.main, 0.06),
																	border: '1px solid',
																	borderColor: alpha(theme.palette.warning.main, 0.2),
																	borderRadius: 1.5,
																	p: 2,
																}}>
																<Stack
																	direction="row"
																	spacing={1}
																	alignItems="center"
																	sx={{ mb: 1 }}>
																	<IconWarning sx={{ fontSize: 18, color: 'warning.dark' }} />
																	<Typography
																		variant="subtitle2"
																		sx={{ fontWeight: 600, color: 'warning.dark' }}>
																		Key Risks
																	</Typography>
																</Stack>
																<Typography
																	variant="body1"
																	sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
																	{safeString(persona.keyRisks)}
																</Typography>
															</Paper>
														)}

														{/* Per-Section Raw JSON Toggle */}
														{persona.rawJson && (
															<Box>
																<Button
																	size="small"
																	variant={expandedRawJson.has(idx) ? 'contained' : 'outlined'}
																	onClick={() => {
																		setExpandedRawJson((prev) => {
																			const newSet = new Set(prev);
																			if (newSet.has(idx)) {
																				newSet.delete(idx);
																			} else {
																				newSet.add(idx);
																			}
																			return newSet;
																		});
																	}}
																	sx={{
																		textTransform: 'none',
																		fontWeight: 500,
																		mb: expandedRawJson.has(idx) ? 1 : 0,
																	}}>
																	{expandedRawJson.has(idx) ? 'Hide Raw Data' : 'View Raw Data'}
																</Button>
																{expandedRawJson.has(idx) && (
																	<Paper
																		elevation={0}
																		sx={{
																			bgcolor: '#1e1e1e',
																			borderRadius: 2,
																			p: 2,
																			maxHeight: 400,
																			overflow: 'auto',
																		}}>
																		<Typography
																			component="pre"
																			sx={{
																				fontFamily: 'monospace',
																				fontSize: '1.35rem',
																				color: '#d4d4d4',
																				margin: 0,
																				whiteSpace: 'pre-wrap',
																				wordBreak: 'break-word',
																			}}>
																			{JSON.stringify(persona.rawJson, null, 2)}
																		</Typography>
																	</Paper>
																)}
															</Box>
														)}
													</Stack>
												</AccordionDetails>
											</Accordion>
										))}
									</>
								);
							})()}

							{/* Inline skeleton placeholders for stages still processing */}
							{!completedStages.has('gold') && (
								<Paper
									elevation={0}
									sx={{
										border: '1px dashed',
										borderColor: alpha('#7c3aed', 0.3),
										borderRadius: 3,
										overflow: 'hidden',
										animation: `${pulseAnimation} 2s ease-in-out infinite`,
									}}>
									<Box sx={{ px: 3, py: 2, bgcolor: alpha('#7c3aed', 0.03) }}>
										<Stack direction="row" alignItems="center" spacing={1.5}>
											<Skeleton variant="circular" width={32} height={32} animation="wave" />
											<Box sx={{ flex: 1 }}>
												<Skeleton variant="text" width={160} height={24} animation="wave" />
												<Skeleton variant="text" width={90} height={18} animation="wave" />
											</Box>
										</Stack>
									</Box>
									<Box sx={{ px: 3, py: 2.5 }}>
										<Skeleton
											variant="text"
											width={80}
											height={20}
											animation="wave"
											sx={{ mb: 1 }}
										/>
										<Skeleton variant="text" width="100%" animation="wave" />
										<Skeleton variant="text" width="85%" animation="wave" />
										<Skeleton variant="text" width="60%" animation="wave" />
									</Box>
								</Paper>
							)}
						</>
					)}
				</Stack>
			</Box>
		);
	};

	if (!currentDocument) return null;

	return (
		<Dialog
			fullWidth
			maxWidth={false}
			open={isOpen}
			onClose={handleDialogClose}
			PaperProps={{
				sx: {
					borderRadius: 3,
					height: '90vh',
					maxHeight: '90vh',
					width: '90vw',
				},
			}}>
			{/* Header */}
			<Box
				sx={{
					alignItems: 'center',
					borderBottom: `1px solid ${theme.palette.divider}`,
					display: 'flex',
					justifyContent: 'space-between',
					px: 3,
					py: 2,
				}}>
				<Stack direction="row" alignItems="center" spacing={2}>
					{getFileIconFromName(docName)}
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
							{docName}
						</Typography>
						<Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
							{sourceConfig && (
								<Chip
									icon={<sourceConfig.icon sx={{ fontSize: '14px !important' }} />}
									label={sourceConfig.label}
									size="small"
									sx={{
										bgcolor: alpha(sourceColor, 0.1),
										color: sourceColor,
										fontWeight: 500,
										height: 24,
										'& .MuiChip-icon': {
											color: sourceColor,
										},
									}}
								/>
							)}
							{docSourceLabel && (
								<Typography variant="body2" color="text.secondary">
									{docSourceLabel}
								</Typography>
							)}
							{(docSourceType || docSourceLabel) && docUploadedAt && (
								<Typography variant="body2" color="text.secondary">
									•
								</Typography>
							)}
							{docUploadedAt && (
								<Typography variant="body2" color="text.secondary">
									Uploaded {formatDocumentDate(docUploadedAt)} by {docUploadedBy}
								</Typography>
							)}
						</Stack>
					</Box>
				</Stack>

				<Stack direction="row" alignItems="center" spacing={1}>
					{/* Navigation */}
					{documents.length > 1 && (
						<Stack direction="row" alignItems="center" spacing={0.5} sx={{ mr: 2 }}>
							<IconButton onClick={handlePrev} disabled={!hasPrev} size="small">
								<IconPrev />
							</IconButton>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ minWidth: 60, textAlign: 'center' }}>
								{currentIndex + 1} of {documents.length}
							</Typography>
							<IconButton onClick={handleNext} disabled={!hasNext} size="small">
								<IconNext />
							</IconButton>
						</Stack>
					)}

					{/* View Document / View AI Insights toggle */}
					{activeTab === 'insights' ? (
						<Button
							variant="outlined"
							size="small"
							startIcon={<IconDoc />}
							onClick={() => {
								setActiveTab('document');
								onTabChange?.('document');
							}}
							sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
							View Document
						</Button>
					) : (
						currentJobId && (
							<Button
								variant="contained"
								size="small"
								startIcon={<IconAI />}
								onClick={() => {
									setActiveTab('insights');
									onTabChange?.('insights');
								}}
								sx={{
									background: `linear-gradient(135deg, ${COLORS.ai.main} 0%, ${COLORS.primary.main} 100%)`,
									borderRadius: 2,
									textTransform: 'none',
									fontWeight: 600,
								}}>
								View AI Insights
							</Button>
						)
					)}

					{/* Download */}
					<Button
						variant="outlined"
						size="small"
						startIcon={<IconDownload />}
						onClick={handleDownload}
						disabled={!blobObjectUrl}
						sx={{ borderRadius: 2, textTransform: 'none' }}>
						Download
					</Button>

					{/* Close */}
					<IconButton onClick={handleDialogClose} size="small">
						<IconClose />
					</IconButton>
				</Stack>
			</Box>

			{/* Content — document viewer + right sidebar */}
			<Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
				{activeTab === 'document' ? (
					<>
						{/* Document Viewer — fills available space */}
						<Box sx={{ bgcolor: 'grey.200', flex: 1, overflow: 'hidden' }}>
							{renderContentViewer()}
						</Box>

						{renderDetailsSidebar()}
					</>
				) : (
					<>
						{/* Insights panel */}
						<Box
							sx={{
								bgcolor: 'background.default',
								flex: 1,
								overflow: 'hidden',
							}}>
							{renderInsightsContent()}
						</Box>

						{renderDetailsSidebar()}
					</>
				)}
			</Box>

			{/* Floating chat popover — bottom-right of dialog, outside content flow */}
			{currentJobId && (
				<Box
					sx={{
						position: 'absolute',
						bottom: 0,
						right: 0,
						pointerEvents: 'none',
						zIndex: 10,
					}}>
					{/* Chat FAB */}
					<Fab
						color="primary"
						onClick={() => {
							setIsChatOpen(true);
							setHasUnreadResponse(false);
						}}
						sx={{
							position: 'absolute',
							bottom: 24,
							right: 24,
							pointerEvents: isChatOpen ? 'none' : 'auto',
							opacity: isChatOpen ? 0 : 1,
							transform: isChatOpen ? 'scale(0)' : 'scale(1)',
							transition: 'opacity 0.2s, transform 0.2s',
							background: `linear-gradient(135deg, ${COLORS.ai.main} 0%, ${COLORS.primary.main} 100%)`,
							boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
							'&:hover': {
								background: `linear-gradient(135deg, ${COLORS.ai.main} 20%, ${COLORS.primary.main} 100%)`,
							},
							// Unread red dot via pseudo-element
							'&::after':
								hasUnreadResponse && !isChatOpen
									? {
											content: '""',
											position: 'absolute',
											top: 2,
											right: 2,
											width: 14,
											height: 14,
											borderRadius: '50%',
											backgroundColor: '#d32f2f',
											border: '2px solid white',
											zIndex: 1,
										}
									: { display: 'none' },
						}}>
						{isChatLoading ? (
							<CircularProgress size={24} thickness={3} sx={{ color: '#ffffff !important' }} />
						) : (
							<IconChat />
						)}
					</Fab>

					{/* Chat popover panel — CSS-only animation, no Slide */}
					<Paper
						elevation={8}
						sx={{
							position: 'absolute',
							bottom: 16,
							right: 16,
							width: isChatExpanded ? 'min(960px, calc(100vw - 80px))' : 560,
							height: isChatExpanded ? 'calc(100vh - 160px)' : 620,
							maxHeight: 'calc(100vh - 120px)',
							borderRadius: 3,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
							pointerEvents: isChatOpen ? 'auto' : 'none',
							opacity: isChatOpen ? 1 : 0,
							transform: isChatOpen ? 'translateY(0)' : 'translateY(20px)',
							transition:
								'opacity 0.25s ease, transform 0.25s ease, width 0.2s ease, height 0.2s ease',
						}}>
						{/* Chat header */}
						<Box
							sx={{
								px: 2,
								py: 1.5,
								borderBottom: '1px solid',
								borderColor: 'divider',
								background: `linear-gradient(135deg, ${alpha(COLORS.ai.main, 0.08)} 0%, ${alpha(COLORS.primary.main, 0.08)} 100%)`,
							}}>
							<Stack direction="row" alignItems="center" justifyContent="space-between">
								<Stack direction="row" alignItems="center" spacing={1}>
									<IconAI sx={{ fontSize: 20, color: COLORS.ai.main }} />
									<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
										Ask About This Document
									</Typography>
								</Stack>
								<Stack direction="row" alignItems="center" spacing={0.5}>
									<IconButton
										size="small"
										onClick={() => setIsChatExpanded((v) => !v)}
										title={isChatExpanded ? 'Collapse' : 'Expand'}>
										{isChatExpanded ? (
											<IconCollapseChat sx={{ fontSize: 18 }} />
										) : (
											<IconExpandChat sx={{ fontSize: 18 }} />
										)}
									</IconButton>
									<IconButton size="small" onClick={() => setIsChatOpen(false)}>
										<IconClose sx={{ fontSize: 18 }} />
									</IconButton>
								</Stack>
							</Stack>
							<Typography variant="caption" color="text.secondary">
								Grounded in this document&apos;s processed outputs
							</Typography>
						</Box>
						{/* Chat body */}
						<Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
							<AiChat
								onSend={handleDocumentChatSend}
								placeholder="Ask about clauses, obligations, dates, amounts, risks…"
								welcomeMessage="Ask me anything about this document — obligations, payment terms, dates, or risks."
								suggestions={[
									'Summarize the key obligations in this document',
									'What deadlines or dates are mentioned?',
									'List payment/disbursement-related items',
									'What are the main risks or caveats?',
								]}
								maxHeight="100%"
								onLoadingChange={setIsChatLoading}
								onResponse={handleChatResponse}
								disabled={!insightsLoaded || insightsLoading}
								disabledMessage="Loading document insights…"
							/>
						</Box>
					</Paper>
				</Box>
			)}
		</Dialog>
	);
};
